import React, { useState, useEffect } from "react";
import { Plus, MapPin, Clock, Calendar, AlertCircle, ArrowLeft, Package } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate, useLocation, useParams } from "react-router-dom";

const ExperiencePreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [selectedActivity, setSelectedActivity] = useState(location.state?.activity || null);
  const [loading, setLoading] = useState(!location.state?.activity);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(null);

  useEffect(() => {
    fetchLatestActivity();
  }, [id]);

  const fetchLatestActivity = async () => {
    try {
      const token = localStorage.getItem("token_partner_activities");
      if (!token) { navigate("/login"); return; }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/task/available-activities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const activities = response.data.data || [];
      const currentActivity = activities.find(a => a._id === id);
      
      if (currentActivity) {
        setSelectedActivity(currentActivity);
      } else if (!selectedActivity) {
        toast.error("Activity not found.");
        navigate('/activity/add-experience');
      }
    } catch (error) {
      console.error(error);
      if (!selectedActivity) {
        toast.error("Failed to load activity details.");
        navigate('/activity/add-experience');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading || !selectedActivity) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)] space-y-6">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary" />
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] animate-pulse">Loading Asset...</p>
      </div>
    );
  }

  const handleAddActivity = async (activity) => {
    const roleid = localStorage.getItem("id_partner_activities");
    const token = localStorage.getItem("token_partner_activities");

    if (!token) { navigate("/login"); return; }

    const payload = { superadmin_activity_id: activity._id, activity_id: roleid };

    if (activity.packages && activity.packages.length > 0) {
      if (selectedPackageIndex === null) {
        toast.error("Please select a package before importing.");
        return;
      }
      const selectedPkg = activity.packages[selectedPackageIndex];
      payload.packages = [{
        package_name: selectedPkg.name,
        offered_price: selectedPkg.price || 0
      }];
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/task`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        toast.success("Experience added to your dashboard!");
        navigate("/activity/listing");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Error adding experience");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <button 
            onClick={() => navigate(-1)} // Navigate back to the marketplace
            className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors font-bold text-sm mb-4"
          >
            <ArrowLeft size={16} /> Back to Marketplace
          </button>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight uppercase">
            Asset Preview
          </h1>
          <p className="text-[10px] font-black text-gray-400 mt-1 uppercase tracking-widest">
            Review experience details before importing to your dashboard
          </p>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-sm overflow-hidden flex flex-col border border-gray-100 animate-in zoom-in-95 duration-500">
        {/* Responsive Image Showcase Gallery */}
        <div className="w-full bg-slate-50 p-2 sm:p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 rounded-2xl sm:rounded-3xl overflow-hidden md:h-[280px] lg:h-[340px]">
            {/* Main Hero Image */}
            <div className="h-48 sm:h-60 md:h-full w-full rounded-xl sm:rounded-2xl overflow-hidden bg-slate-100">
              {selectedActivity.imageUrls?.[0] ? (
                <img src={selectedActivity.imageUrls[0]} alt="Hero" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300">
                  <MapPin size={48} />
                </div>
              )}
            </div>

            {/* Gallery Grid (Images 2-5) */}
            {selectedActivity.imageUrls?.slice(1, 5).length > 0 && (
              <div className="grid grid-cols-2 grid-rows-2 h-44 sm:h-52 md:h-full gap-2">
                {selectedActivity.imageUrls.slice(1, 5).map((url, i) => (
                  <div key={i} className="w-full h-full rounded-xl sm:rounded-2xl overflow-hidden bg-slate-100">
                    <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-5 sm:p-8 space-y-6 sm:space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 sm:gap-5">
            <div className="space-y-1.5 sm:space-y-2 flex-1">
              <div className="flex items-center gap-1.5 text-primary">
                <MapPin size={14} />
                <span className="text-[10px] font-black uppercase tracking-widest">{selectedActivity.city?.join(', ') || 'Global'}</span>
              </div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-extrabold text-gray-900 tracking-tight leading-snug uppercase">{selectedActivity.name}</h2>
            </div>
            <div className="bg-primary/5 p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-primary/10 text-center min-w-[160px] sm:min-w-[180px] shrink-0">
              <p className="text-[9px] font-black text-primary/70 uppercase tracking-widest mb-0.5">Standard Asset Rate</p>
              <p className="text-xl sm:text-2xl font-black text-primary">₹{selectedActivity.price}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {[
              { icon: Clock, label: 'Duration', value: '4-6 Hours' },
              { icon: Calendar, label: 'Availability', value: 'Daily' },
              { icon: AlertCircle, label: 'Type', value: 'Curated' }
            ].map((item, i) => (
              <div key={i} className="bg-gray-50/50 p-3.5 sm:p-4 rounded-xl sm:rounded-2xl flex items-center gap-3 border border-gray-100">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-white rounded-lg sm:rounded-xl flex items-center justify-center text-primary shadow-sm shrink-0"><item.icon size={16} /></div>
                <div><p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{item.label}</p><p className="font-extrabold text-gray-900 text-xs sm:text-sm">{item.value}</p></div>
              </div>
            ))}
          </div>

          <div className="space-y-2 sm:space-y-3">
            <h3 className="text-sm sm:text-base font-black text-gray-900 tracking-tight uppercase">Experience Description</h3>
            <p className="text-gray-600 leading-relaxed text-xs sm:text-sm font-medium">{selectedActivity.description}</p>
          </div>

          {selectedActivity.packages && selectedActivity.packages.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm sm:text-base font-black text-gray-900 tracking-tight uppercase flex items-center gap-2">
                <Package className="text-primary" size={18} />
                Available Packages
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedActivity.packages.map((pkg, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => setSelectedPackageIndex(idx)}
                    className={`p-4 sm:p-5 rounded-2xl border flex flex-col gap-3 cursor-pointer transition-all ${selectedPackageIndex === idx ? 'bg-primary/5 border-primary shadow-md' : 'bg-gray-50/50 border-gray-100 hover:shadow-md hover:border-gray-300'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-black text-primary uppercase tracking-widest bg-primary/10 px-2.5 py-0.5 rounded-full">Package {idx + 1}</span>
                      {pkg.soloOrGroup && (
                        <span className="text-[9px] font-black text-gray-400 bg-white px-2.5 py-0.5 rounded-full uppercase tracking-widest border border-gray-200">
                          {pkg.soloOrGroup}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-extrabold text-gray-900 text-xs sm:text-sm uppercase tracking-tight">{pkg.name}</p>
                      {pkg.duration && (
                        <p className="text-[11px] font-semibold text-gray-400 flex items-center gap-1 mt-0.5">
                          <Clock size={11} /> {pkg.duration}
                        </p>
                      )}
                    </div>
                    <div className="pt-3 border-t border-gray-100/50 flex flex-col gap-0.5 mt-auto">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Price</p>
                      <div className="flex items-end gap-1.5">
                        <p className="text-lg sm:text-xl font-black text-primary tracking-tighter">₹{pkg.price}</p>
                        {pkg.discount > 0 && (
                          <p className="text-[10px] font-black text-green-500 uppercase tracking-widest mb-0.5">
                            -{pkg.discount}%
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-8 border-t border-gray-100 bg-gray-50/50 flex gap-4">
          <button onClick={() => navigate(-1)} className="flex-1 py-5 bg-white text-gray-400 font-black rounded-3xl border border-gray-200 hover:bg-gray-100 transition-all uppercase tracking-widest text-[10px]">Back to Marketplace</button>
          <button onClick={() => handleAddActivity(selectedActivity)} className="flex-[2] py-5 bg-primary text-white font-black rounded-3xl shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all uppercase tracking-widest text-[10px] flex items-center justify-center gap-3">Import to Dashboard <Plus size={20} /></button>
        </div>
      </div>
    </div>
  );
};

export default ExperiencePreviewPage;
